import {
  type BlockImage,
  type BlockVisual,
  type BlockWireframe,
  type ColorItem,
  Factory,
  type ID,
  isLibraryRef,
  type LibraryColor,
  type LibrarySymbolData,
  ProjectActions,
  selectLibraryItem,
} from '@octopus-do/octopus-project-core'
import { LibrarySagaActions } from '@store/librarySaga'
import type { AppThunk } from '@store/migrationActions/thunkActions/types'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import type { Action } from 'redux'

import API from '@/api'

// Content edits of a symbol. Kept apart from the rest of the symbol thunks:
// this module is reached from the canvas reducer through the block thunks and
// must not pull the store in.

/**
 * One rule for every edit of a symbol (the content aside, see below). A project symbol takes it as a
 * commit. A library ref sends it to the library when the caller may edit it
 * there — that is what makes the edit show up in every project. When the
 * caller may not (the item is gone from the library, belongs to another
 * workspace, or the caller is a guest or a copywriter), the ref becomes a
 * project symbol first and the edit lands on it.
 */
const thunkEditSymbol =
  (
    id: ID,
    actions: Action[],
    patch: Partial<LibrarySymbolData>,
    // What a ref keeps locally besides the library edit: its palette color,
    // the fallback for a palette without the library hex.
    localActions: Action[] = []
  ): AppThunk =>
  (dispatch, getState) => {
    const state = getState()
    const symbol = migrationProjectSelector.getProjectSymbolById(id)(state)

    if (!symbol) {
      return
    }

    if (!isLibraryRef(symbol)) {
      dispatch(commitProjectRequest(...actions))
      return
    }

    if (migrationProjectSelector.getCanEditSymbolInLibrary(id)(state)) {
      dispatch(
        LibrarySagaActions.updateSymbol({ libraryId: symbol.libraryId, patch })
      )

      if (localActions.length > 0) {
        dispatch(commitProjectRequest(...localActions))
      }

      return
    }

    const data = migrationProjectSelector.getProjectSymbolData(id)(state)

    dispatch(
      commitProjectRequest(
        ProjectActions.symbols.detach({ id, data }),
        ...actions
      )
    )
  }

export const thunkUpdateSymbolTitle =
  ({ id, title }: { id: ID; title: string }): AppThunk =>
  (dispatch) => {
    dispatch(
      thunkEditSymbol(id, [ProjectActions.symbols.updateTitle({ id, title })], {
        title,
      })
    )
  }

/**
 * The content is the exception to the rule: a library ref keeps its own, so
 * the page content of a shared symbol stays a matter of the project.
 */
export const thunkUpdateSymbolContent =
  ({ id, content }: { id: ID; content: string | null }): AppThunk =>
  (dispatch) => {
    dispatch(
      commitProjectRequest(
        ProjectActions.symbols.updateContent({ id, content })
      )
    )
  }

export const thunkUpdateSymbolVisual =
  ({ id, visual }: { id: ID; visual: BlockVisual }): AppThunk =>
  (dispatch) => {
    dispatch(
      thunkEditSymbol(
        id,
        [ProjectActions.symbols.updateVisual({ ids: [id], visual })],
        { visual }
      )
    )
  }

export const thunkUpdateSymbolWireframes =
  ({ id, wireframes }: { id: ID; wireframes: BlockWireframe[] }): AppThunk =>
  (dispatch) => {
    dispatch(
      thunkUpdateSymbolVisual({
        id,
        visual: { wireframes, type: 'wireframes' },
      })
    )
  }

const toLibraryColor = (color: ColorItem | undefined): LibraryColor | null =>
  color ? { hexCode: color.hexCode, title: color.title } : null

/**
 * The color follows the same rule as the rest: a library symbol keeps it as
 * a hex, the project palette entry with that hex is what the ref renders with.
 * The ref also keeps the palette id as a fallback.
 */
export const thunkUpdateSymbolColor =
  ({ id, colorId }: { id: ID; colorId: ID }): AppThunk =>
  (dispatch, getState) => {
    const { colors } = getState().manager.project
    const color = toLibraryColor(colors.find((c) => c.id === colorId))
    const updateColor = ProjectActions.symbols.updateColor({ id, colorId })

    dispatch(thunkEditSymbol(id, [updateColor], { color }, [updateColor]))
  }

/** An edit made on the library card itself, no project symbol involved. */
export const thunkUpdateLibrarySymbol =
  ({
    libraryId,
    patch,
  }: {
    libraryId: ID
    patch: Partial<LibrarySymbolData>
  }): AppThunk =>
  (dispatch) => {
    dispatch(LibrarySagaActions.updateSymbol({ libraryId, patch }))
  }

export const thunkUpdateLibrarySymbolColor =
  ({ libraryId, colorId }: { libraryId: ID; colorId: ID }): AppThunk =>
  (dispatch, getState) => {
    const { colors } = getState().manager.project
    const color = toLibraryColor(colors.find((c) => c.id === colorId))

    dispatch(thunkUpdateLibrarySymbol({ libraryId, patch: { color } }))
  }

/**
 * Gives the palette an entry for every library color the refs of the project
 * use, so the refs render with the library color rather than the fallback.
 * One commit, only when the caller may edit the project.
 */
export const thunkEnsureLibraryColors =
  (): AppThunk => (dispatch, getState) => {
    const state = getState()

    if (!state.manager.meta.access.canEdit) {
      return
    }

    const { colors, symbols } = state.manager.project
    const known = new Set(colors.map(({ hexCode }) => hexCode.toLowerCase()))
    const missing = new Map<string, LibraryColor>()

    symbols.filter(isLibraryRef).forEach((ref) => {
      const color = selectLibraryItem('symbol', ref.libraryId)(state)?.data
        .color

      if (color && !known.has(color.hexCode.toLowerCase())) {
        missing.set(color.hexCode.toLowerCase(), color)
      }
    })

    if (missing.size === 0) {
      return
    }

    const items = [...missing.values()].map(({ hexCode, title }) =>
      Factory.color({ hexCode, id: getId(), title })
    )

    dispatch(commitProjectRequest(ProjectActions.colors.add({ items })))
  }

/** Uploads a picture into the project storage and hands the visual to `apply`. */
const thunkUploadImage =
  (
    file: File,
    apply: (visual: BlockVisual) => AppThunk
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const projectId = migrationProjectSelector.getProjectId(getState())

    try {
      const image: BlockImage = await API.Image.upload({ projectId, file })

      dispatch(apply({ image, type: 'image' }))
    } catch (error) {
      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.UPLOAD_ERROR,
        })
      )

      setTimeout(() => {
        dispatch(
          notificationActionCreators.setClearNotification({
            toastContainerId: 'main',
          })
        )
      }, 3000)

      throw error
    }
  }

export const thunkUploadSymbolImage = ({ id, file }: { id: ID; file: File }) =>
  thunkUploadImage(file, (visual) => thunkUpdateSymbolVisual({ id, visual }))

export const thunkUploadLibrarySymbolImage = ({
  libraryId,
  file,
}: {
  libraryId: ID
  file: File
}) =>
  thunkUploadImage(file, (visual) =>
    thunkUpdateLibrarySymbol({ libraryId, patch: { visual } })
  )
